import { useMemo } from 'react'
import type { AxiosError } from 'axios'
import { useCompanyStatsCommon, useCompanyStatsProducts, useCompanyStatsScores } from './index'
import type { CompanyStatsDataHooks } from './hooks'
import type {
    GetCommonScoresApiV1StatsOrgIdCommonGetResponse,
    GenerationScoreStatsApiV1StatsOrgIdScoresGetResponse,
    GetTopBottomProductsApiV1StatsOrgIdProductsGetResponse,
} from '@/common/api/client/types.gen'

export type BrandComparisonBrandData = {
    id: string
    name: string
    common: GetCommonScoresApiV1StatsOrgIdCommonGetResponse
    scores: GenerationScoreStatsApiV1StatsOrgIdScoresGetResponse
    products: GetTopBottomProductsApiV1StatsOrgIdProductsGetResponse
}

export type CompanyStatsComparisonMode = 'brand' | 'seller'

export type UseCompanyStatsBrandComparisonResult = {
    brandA: BrandComparisonBrandData | null
    brandB: BrandComparisonBrandData | null
    isLoading: boolean
    error: AxiosError | null
    refetch: () => void
    isReady: boolean
}

export const useCompanyStatsBrandComparison = (
    orgId: string | undefined,
    compareBy: CompanyStatsComparisonMode,
    entityAId: string | undefined,
    entityBId: string | undefined,
    entityNames: Record<string, string>,
    hooks?: Pick<CompanyStatsDataHooks, 'useCompanyStatsCommon' | 'useCompanyStatsScores' | 'useCompanyStatsProducts'>
): UseCompanyStatsBrandComparisonResult => {
    const dataHooks = hooks ?? {
        useCompanyStatsCommon,
        useCompanyStatsScores,
        useCompanyStatsProducts,
    }
    const isReady = Boolean(orgId && entityAId && entityBId && entityAId !== entityBId)
    const entityFilterA = compareBy === 'brand' ? { brand: entityAId } : { seller: entityAId }
    const entityFilterB = compareBy === 'brand' ? { brand: entityBId } : { seller: entityBId }

    const commonA = dataHooks.useCompanyStatsCommon(orgId, entityFilterA, isReady)
    const scoresA = dataHooks.useCompanyStatsScores(orgId, entityFilterA, isReady)
    const productsA = dataHooks.useCompanyStatsProducts(orgId, entityFilterA, isReady)

    const commonB = dataHooks.useCompanyStatsCommon(orgId, entityFilterB, isReady)
    const scoresB = dataHooks.useCompanyStatsScores(orgId, entityFilterB, isReady)
    const productsB = dataHooks.useCompanyStatsProducts(orgId, entityFilterB, isReady)

    const isLoading =
        commonA.isLoading ||
        scoresA.isLoading ||
        productsA.isLoading ||
        commonB.isLoading ||
        scoresB.isLoading ||
        productsB.isLoading

    const error =
        commonA.error ?? scoresA.error ?? productsA.error ?? commonB.error ?? scoresB.error ?? productsB.error ?? null

    const brandA = useMemo((): BrandComparisonBrandData | null => {
        if (!isReady || !entityAId || !commonA.data || !scoresA.data || !productsA.data) return null
        return {
            id: entityAId,
            name: entityNames[entityAId] ?? entityAId,
            common: commonA.data,
            scores: scoresA.data,
            products: productsA.data,
        }
    }, [commonA.data, entityAId, entityNames, isReady, productsA.data, scoresA.data])

    const brandB = useMemo((): BrandComparisonBrandData | null => {
        if (!isReady || !entityBId || !commonB.data || !scoresB.data || !productsB.data) return null
        return {
            id: entityBId,
            name: entityNames[entityBId] ?? entityBId,
            common: commonB.data,
            scores: scoresB.data,
            products: productsB.data,
        }
    }, [commonB.data, entityBId, entityNames, isReady, productsB.data, scoresB.data])

    const refetch = () => {
        void commonA.refetch()
        void scoresA.refetch()
        void productsA.refetch()
        void commonB.refetch()
        void scoresB.refetch()
        void productsB.refetch()
    }

    return {
        brandA,
        brandB,
        isLoading,
        error,
        refetch,
        isReady,
    }
}
